/**
 * Screen 01's fetches — or rather, the lack of them.
 *
 * The dashboard reads other people's data, so there is not a single URL in
 * this file: the calls are the ones each feature already exports —
 * `stockApi`, `prepApi`, `menuApi`, `wastageApi`, `expensesApi`, `ordersApi`
 * — and what lives here is the part around them. Repeating those paths would
 * mean the dashboard quietly pointing at an old route the day one of them
 * moves.
 *
 * What it does own is `attempt`, which turns a failed call into a tile that
 * says why rather than a screen that says nothing. Eight tiles read eight
 * features; one of them being unbuilt, unreachable, or closed to this role
 * must not take the other seven down with it.
 */

import { ApiError } from "../api.js";

/**
 * What one tile knows.
 *
 * `unavailable` is not an error: it is a route that has not landed yet, a
 * venue with nothing recorded, or a role that does not see this. The tile
 * says so in the same voice it would say a number, rather than in red.
 */
export type Tile<T> =
  | { readonly state: "loading" }
  | { readonly state: "ok"; readonly value: T }
  | { readonly state: "unavailable"; readonly reason: string }
  | { readonly state: "error"; readonly message: string };

export const LOADING: Tile<never> = { state: "loading" };

/**
 * Run a call and describe how it went.
 *
 * A 404 means the thing is not there — an unbuilt route, or a count that has
 * since been deleted — and reads as whatever `notThere` says. A 403 is a role
 * answer and says so plainly, because a tile going red at a bartender for
 * asking a manager's question would be the screen's fault, not theirs.
 * Anything else is a real error and shows its message.
 */
export async function attempt<T>(
  call: () => Promise<T>,
  notThere = "Not available yet",
): Promise<Tile<T>> {
  try {
    return { state: "ok", value: await call() };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) return { state: "unavailable", reason: notThere };
      if (err.status === 403) {
        return { state: "unavailable", reason: "Your role does not show this" };
      }
      return { state: "error", message: err.message };
    }
    return {
      state: "error",
      message: err instanceof Error ? err.message : "Could not load",
    };
  }
}
